import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    updateDoc,
    deleteDoc,
    doc,
    query,
    where,
    orderBy
} from 'firebase/firestore';
import { db } from '../../../config/firebase';

export const savingsService = {
    // Thêm khoản tiết kiệm
    async addSavings(userId, savingsData) {
        try {
            const docRef = await addDoc(collection(db, `users/${userId}/savings`), {
                ...savingsData,
                soTienHienTai: savingsData.soTienGoc,
                laiSuatTichLuy: 0,
                ngayTao: new Date(),
                ngayCapNhat: new Date(),
                trangThai: 'active'
            });
            return docRef.id;
        } catch (error) {
            console.error('Error adding savings:', error);
            throw error;
        }
    },

    // Lấy danh sách tiết kiệm
    async getSavings(userId) {
        try {
            const q = query(
                collection(db, `users/${userId}/savings`),
                orderBy('ngayTao', 'desc')
            );
            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                ngayTao: doc.data().ngayTao?.toDate(),
                ngayDaoHan: doc.data().ngayDaoHan?.toDate(),
                ngayCapNhat: doc.data().ngayCapNhat?.toDate()
            }));
        } catch (error) {
            console.error('Error fetching savings:', error);
            throw error;
        }
    },

    // Tính lãi suất theo thời gian thực
    calculateCurrentValue(saving) {
        const now = new Date();
        const startDate = new Date(saving.ngayTao);
        const daysPassed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));

        const principal = saving.soTienGoc;
        const annualRate = saving.laiSuat / 100;
        const timeInYears = daysPassed / 365;

        let currentValue;
        let interestEarned;

        switch (saving.loaiLai) {
            case 'simple':
                // Lãi đơn: A = P(1 + rt)
                currentValue = principal * (1 + annualRate * timeInYears);
                break;
            case 'monthly':
                // Lãi kép hàng tháng
                const monthlyRate = annualRate / 12;
                const months = daysPassed / 30.44;
                currentValue = principal * Math.pow(1 + monthlyRate, months);
                break;
            case 'quarterly':
                // Lãi kép hàng quý
                const quarterlyRate = annualRate / 4;
                const quarters = daysPassed / 91.25;
                currentValue = principal * Math.pow(1 + quarterlyRate, quarters);
                break;
            case 'yearly':
                // Lãi kép hàng năm
                currentValue = principal * Math.pow(1 + annualRate, timeInYears);
                break;
            default:
                currentValue = principal;
        }

        interestEarned = currentValue - principal;

        return {
            currentValue: Math.round(currentValue),
            interestEarned: Math.round(interestEarned),
            daysPassed,
            effectiveRate: principal > 0 ? ((currentValue / principal - 1) * 100) : 0
        };
    },

    // Rút tiền tiết kiệm
    async withdrawSavings(userId, savingsId, withdrawAmount, reason = '') {
        try {
            const savingsRef = doc(db, `users/${userId}/savings`, savingsId);
            const savingsDoc = await getDoc(savingsRef);

            if (!savingsDoc.exists()) {
                throw new Error('Khoản tiết kiệm không tồn tại');
            }

            const savingsData = savingsDoc.data();
            const currentValue = this.calculateCurrentValue(savingsData);

            if (withdrawAmount > currentValue.currentValue) {
                throw new Error('Số tiền rút vượt quá số dư hiện tại');
            }

            const newBalance = currentValue.currentValue - withdrawAmount;
            const isFullyWithdrawn = newBalance <= 0;

            // Cập nhật khoản tiết kiệm
            await updateDoc(savingsRef, {
                soTienHienTai: newBalance,
                trangThai: isFullyWithdrawn ? 'withdrawn' : 'active',
                ngayCapNhat: new Date()
            });

            // Tạo lịch sử giao dịch
            await addDoc(collection(db, `users/${userId}/savingsTransactions`), {
                savingsId: savingsId,
                loaiGiaoDich: 'withdraw',
                soTien: withdrawAmount,
                soDuSauGiaoDich: newBalance,
                lyDo: reason,
                ngayGiaoDich: new Date()
            });

            return {
                newBalance,
                withdrawAmount,
                isFullyWithdrawn
            };

        } catch (error) {
            console.error('Error withdrawing savings:', error);
            throw error;
        }
    },

    // Nạp thêm tiền vào khoản tiết kiệm
    async depositToSavings(userId, savingsId, depositAmount) {
        try {
            const savingsRef = doc(db, `users/${userId}/savings`, savingsId);
            const savingsDoc = await getDoc(savingsRef);

            if (!savingsDoc.exists()) {
                throw new Error('Khoản tiết kiệm không tồn tại');
            }

            const savingsData = savingsDoc.data();
            const currentValue = this.calculateCurrentValue(savingsData);
            const newBalance = currentValue.currentValue + depositAmount;

            // Cập nhật khoản tiết kiệm
            await updateDoc(savingsRef, {
                soTienHienTai: newBalance,
                ngayCapNhat: new Date()
            });

            // Tạo lịch sử giao dịch
            await addDoc(collection(db, `users/${userId}/savingsTransactions`), {
                savingsId: savingsId,
                loaiGiaoDich: 'deposit',
                soTien: depositAmount,
                soDuSauGiaoDich: newBalance,
                ngayGiaoDich: new Date()
            });

            return {
                newBalance,
                depositAmount
            };

        } catch (error) {
            console.error('Error depositing to savings:', error);
            throw error;
        }
    },

    // Lấy lịch sử giao dịch tiết kiệm
    async getSavingsTransactions(userId, savingsId = null) {
        try {
            let q;
            if (savingsId) {
                q = query(
                    collection(db, `users/${userId}/savingsTransactions`),
                    where('savingsId', '==', savingsId),
                    orderBy('ngayGiaoDich', 'desc')
                );
            } else {
                q = query(
                    collection(db, `users/${userId}/savingsTransactions`),
                    orderBy('ngayGiaoDich', 'desc')
                );
            }

            const querySnapshot = await getDocs(q);
            return querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                ngayGiaoDich: doc.data().ngayGiaoDich?.toDate()
            }));
        } catch (error) {
            console.error('Error fetching savings transactions:', error);
            throw error;
        }
    }
};
